import { useEffect } from 'react';
import { startOfDay } from 'date-fns';
import { DateRange } from '../../models/monitor/DateRange';
import { getDateRange } from '../../api/services/monitor/MonitorService';
import { StandardPeriodType } from '../../types/filters/StandardPeriodType';
import { calculateDefaultDateRange, toLocalDay } from '../../helpers/filters/DateRangeHelper';

const useDateRange = (
  projectId: number,
  setDateRange: (dateRange: DateRange | null) => void,
  setRange: (range: (Date | null)[] | null) => void,
  setSelectedPeriod: (period: StandardPeriodType | null) => void
) => {
  useEffect(() => {
    if (!projectId || projectId === 0) {
      return;
    }

    // On a project switch, range/dateRange are already nulled in FiltersContextProvider's RENDER
    // (an effect here would run one render too late — after child queries have fired).
    // Two quick switches race their /date-range responses; the flag drops the stale one.
    let cancelled = false;

    // range starts as null — without a fallback the page would never fire its queries.
    const applyFallbackRange = () => {
      const { period, range } = calculateDefaultDateRange();
      setRange(range);
      setSelectedPeriod(period);
    };

    getDateRange(projectId)
      .then((dateRange) => {
        if (cancelled) return;

        const oldestDateLocal = toLocalDay(dateRange.oldestDate);
        const newestDateLocal = toLocalDay(dateRange.newestDate);

        if (!oldestDateLocal || !newestDateLocal) {
          console.error('Invalid dates after conversion:', {
            oldestDate: dateRange.oldestDate,
            newestDate: dateRange.newestDate,
          });
          applyFallbackRange();
          return;
        }

        const today = startOfDay(new Date());

        setDateRange({
          oldestDate: oldestDateLocal,
          newestDate: today,
        });

        const { period, range } = calculateDefaultDateRange(oldestDateLocal);

        setRange(range);
        setSelectedPeriod(period);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to get date range:', error);
        applyFallbackRange();
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);
};

export default useDateRange;
